import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  Timestamp,
  limit,
  orderBy
} from 'firebase/firestore';
import { db } from './config';
import { format, startOfDay, endOfDay, getDay } from 'date-fns';

const TIME_BLOCKS_COLLECTION = 'timeBlocks';
const BOOKINGS_COLLECTION = 'bookings';
const EVENTS_COLLECTION = 'events';
const AVAILABILITY_COLLECTION = 'availability';
const SETTINGS_DOC = 'settings/booking';

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Default booking settings
const DEFAULT_BOOKING_SETTINGS = {
  bufferTimeBefore: 30, // minutes before a booking that should be blocked
  bufferTimeAfter: 30,  // minutes after a booking that should be blocked
  allowCustomTimeRequests: true, // allow clients to request custom times
  defaultSlotDuration: 60 // default slot duration in minutes
};

/**
 * Parse duration string to minutes (handles various formats)
 * Examples: "60 min", "1 hr", "1.5 hr", "90 minutes", "1 hour 30 min", "1h 30m"
 */
const parseDurationToMinutes = (duration, defaultMinutes = 60) => {
  if (!duration) return defaultMinutes;
  if (typeof duration === 'number') return duration;

  const durationStr = duration.toString().toLowerCase().trim();
  let totalMinutes = 0;

  // Try to match hours (hr, hrs, hour, hours, h)
  const hourMatch = durationStr.match(/(\d+\.?\d*)\s*(hr|hrs|hour|hours|h)\b/);
  if (hourMatch) {
    totalMinutes += parseFloat(hourMatch[1]) * 60;
  }

  // Try to match minutes (min, mins, minute, minutes, m) - but not if it's part of "month"
  const minMatch = durationStr.match(/(\d+\.?\d*)\s*(min|mins|minute|minutes|m)\b/);
  if (minMatch && !durationStr.includes('month')) {
    totalMinutes += parseFloat(minMatch[1]);
  }

  // If we found something, return it
  if (totalMinutes > 0) {
    return Math.round(totalMinutes);
  }

  // Fallback: try to extract any number
  const numMatch = durationStr.match(/(\d+\.?\d*)/);
  if (numMatch) {
    const num = parseFloat(numMatch[1]);
    // If number is small (< 10), assume hours; otherwise minutes
    return num < 10 ? Math.round(num * 60) : Math.round(num);
  }

  return defaultMinutes;
};

/**
 * Convert time string (e.g., "14:30") to hour slots it occupies
 */
const timeToHourSlots = (startTime, endTime) => {
  const slots = [];
  if (!startTime) return slots;

  const startHour = parseInt(startTime.split(':')[0], 10);
  const endHour = endTime ? parseInt(endTime.split(':')[0], 10) : startHour + 1;
  const endMinutes = endTime ? parseInt(endTime.split(':')[1], 10) : 0;

  for (let h = startHour; h < endHour; h++) {
    slots.push(`${h.toString().padStart(2, '0')}:00`);
  }

  // Include end hour if event extends into it
  if (endMinutes > 0) {
    slots.push(`${endHour.toString().padStart(2, '0')}:00`);
  }

  return slots;
};

/**
 * Generate time slots between start and end time
 */
const generateTimeSlots = (startTime, endTime, interval = 60) => {
  const slots = [];
  if (!startTime || !endTime) return slots;

  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;

  // Use < (not <=) so the end time itself is NOT a bookable slot start.
  // e.g. if work ends at 16:00, last 1hr slot starts at 15:00 (ends at 16:00)
  for (let m = startMinutes; m < endMinutes; m += interval) {
    const hour = Math.floor(m / 60);
    const minute = m % 60;
    slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
  }

  return slots;
};

/**
 * Convert time string to minutes from midnight
 */
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

export const availabilityService = {
  /**
   * Get booking settings (buffer times, custom requests, etc.)
   */
  async getBookingSettings() {
    try {
      const docRef = doc(db, ...SETTINGS_DOC.split('/'));
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return DEFAULT_BOOKING_SETTINGS;
      }

      return {
        ...DEFAULT_BOOKING_SETTINGS,
        ...docSnap.data()
      };
    } catch (error) {
      console.error('Error fetching booking settings:', error);
      return DEFAULT_BOOKING_SETTINGS;
    }
  },
  /**
   * Get the practitioner's regular working schedule
   * Returns the weekly schedule with enabled days and hours
   */
  async getWorkingSchedule() {
    try {
      // Get the most recently updated availability schedule
      // This ensures admin changes are always picked up correctly
      let snapshot = await getDocs(query(
        collection(db, AVAILABILITY_COLLECTION),
        orderBy('updatedAt', 'desc'),
        limit(1)
      ));

      // Fall back to any document if none have updatedAt (legacy data)
      if (snapshot.empty) {
        snapshot = await getDocs(query(
          collection(db, AVAILABILITY_COLLECTION),
          limit(1)
        ));
      }

      if (snapshot.empty) {
        // Return default schedule if none exists
        return {
          monday: { enabled: true, start: '09:00', end: '17:00' },
          tuesday: { enabled: true, start: '09:00', end: '17:00' },
          wednesday: { enabled: true, start: '09:00', end: '17:00' },
          thursday: { enabled: true, start: '09:00', end: '17:00' },
          friday: { enabled: true, start: '09:00', end: '17:00' },
          saturday: { enabled: false, start: '09:00', end: '13:00' },
          sunday: { enabled: false, start: '09:00', end: '13:00' }
        };
      }

      const docData = snapshot.docs[0].data();
      return docData.regularSchedule || null;
    } catch (error) {
      console.error('Error fetching working schedule:', error);
      return null;
    }
  },

  /**
   * Check if a specific day is enabled for bookings
   */
  async isDayEnabled(date) {
    const schedule = await this.getWorkingSchedule();
    if (!schedule) return true; // Default to enabled if no schedule

    const dayIndex = getDay(date);
    const dayName = DAY_NAMES[dayIndex];
    const daySchedule = schedule[dayName];

    return daySchedule?.enabled || false;
  },

  /**
   * Get the working hours for a specific day
   */
  async getWorkingHoursForDay(date) {
    const schedule = await this.getWorkingSchedule();
    if (!schedule) {
      // Default hours
      return { start: '09:00', end: '17:00', enabled: true };
    }

    const dayIndex = getDay(date);
    const dayName = DAY_NAMES[dayIndex];
    const daySchedule = schedule[dayName];

    if (!daySchedule?.enabled) {
      return { start: null, end: null, enabled: false };
    }

    return {
      start: daySchedule.start || '09:00',
      end: daySchedule.end || '17:00',
      enabled: true
    };
  },

  /**
   * Get available time slots for a specific day based on working hours
   */
  async getAvailableTimeSlotsForDay(date, interval = 60) {
    const workingHours = await this.getWorkingHoursForDay(date);

    if (!workingHours.enabled) {
      return [];
    }

    return generateTimeSlots(workingHours.start, workingHours.end, interval);
  },

  /**
   * Get availability for a specific date
   * Returns blocked time slots, booked time slots, buffer-blocked slots, event-blocked slots, and booking counts per slot
   * IMPORTANT: Time slots during scheduled events are blocked!
   * IMPORTANT: Buffer time before and after bookings are also blocked!
   */
  async getAvailabilityForDate(date) {
    const startDate = startOfDay(date);
    const endDate = endOfDay(date);
    const dateString = format(date, 'yyyy-MM-dd');

    try {
      // Get booking settings for buffer times
      const bookingSettings = await this.getBookingSettings();
      const { bufferTimeBefore, bufferTimeAfter, allowCustomTimeRequests, defaultSlotDuration } = bookingSettings;

      // Check if day is enabled first
      const workingHours = await this.getWorkingHoursForDay(date);
      const isDayEnabled = workingHours.enabled;

      // Generate all possible time slots for the day (needed for buffer calculation)
      const allTimeSlots = workingHours.enabled
        ? generateTimeSlots(workingHours.start, workingHours.end, defaultSlotDuration)
        : [];

      // 1. Get blocked times from timeBlocks collection (admin My Availability)
      // Query for blocks that OVERLAP with this date (catches multi-day blocks).
      // A block overlaps if it starts before the day ends AND ends after the day starts.
      // Firestore only allows range queries on one field, so we query blocks that
      // haven't ended before today and filter the start in code.
      const blockedQuery = query(
        collection(db, TIME_BLOCKS_COLLECTION),
        where('endDate', '>=', Timestamp.fromDate(startDate))
      );

      const blockedSnapshot = await getDocs(blockedQuery);
      const blockedSlots = [];

      blockedSnapshot.forEach(doc => {
        const data = doc.data();
        const blockStart = data.startDate.toDate();
        const blockEnd = data.endDate.toDate();

        // Skip blocks that start after today ends (second half of overlap check)
        if (blockStart > endDate) return;

        // If marked as all-day block, block all working hour slots
        if (data.allDay) {
          allTimeSlots.forEach(slot => {
            if (!blockedSlots.includes(slot)) {
              blockedSlots.push(slot);
            }
          });
          return;
        }

        // Check if this block spans the entire day (multi-day block covering this date)
        const blockStartDay = startOfDay(blockStart);
        const blockEndDay = startOfDay(blockEnd);
        const thisDay = startOfDay(date);

        if (blockStartDay < thisDay && blockEndDay > thisDay) {
          // Multi-day block that fully covers this day — block ALL working hour slots
          allTimeSlots.forEach(slot => {
            if (!blockedSlots.includes(slot)) {
              blockedSlots.push(slot);
            }
          });
          return;
        }

        // For same-day blocks or blocks that start/end on this day,
        // block the specific hour slots
        const startHour = blockStartDay < thisDay ? 0 : blockStart.getHours();
        const endHour = blockEndDay > thisDay ? 24 : blockEnd.getHours();
        const endMinutesVal = blockEnd.getMinutes();

        for (let h = startHour; h < endHour; h++) {
          const slot = `${h.toString().padStart(2, '0')}:00`;
          if (!blockedSlots.includes(slot)) {
            blockedSlots.push(slot);
          }
        }

        // Include end hour if block extends into it (partial hour)
        if (blockEndDay <= thisDay && endMinutesVal > 0) {
          const slot = `${endHour.toString().padStart(2, '0')}:00`;
          if (!blockedSlots.includes(slot)) {
            blockedSlots.push(slot);
          }
        }
      });

      // 2. Get events scheduled for this date - BLOCK THESE TIME SLOTS
      const eventsQuery = query(
        collection(db, EVENTS_COLLECTION),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate))
      );

      const eventsSnapshot = await getDocs(eventsQuery);
      const eventBlockedSlots = [];
      const events = [];

      eventsSnapshot.forEach(doc => {
        const data = doc.data();
        // Only block for active events (not cancelled/closed)
        if (data.status !== 'cancelled' && data.status !== 'closed') {
          const eventDate = data.date?.toDate?.() || new Date(data.date);
          events.push({
            id: doc.id,
            title: data.title,
            time: data.time,
            endTime: data.endTime,
            date: eventDate
          });

          // Block all hourly slots that this event occupies
          const eventSlots = timeToHourSlots(data.time, data.endTime);
          eventBlockedSlots.push(...eventSlots);
        }
      });

      // 3. Get booked slots from bookings collection
      const bookingsQuery = query(
        collection(db, BOOKINGS_COLLECTION),
        where('dateString', '==', dateString),
        where('status', 'in', ['confirmed', 'pending'])
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookedSlots = [];
      const bookingCounts = {};
      const bookingsData = [];

      bookingsSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data.time) {
          // Parse duration for buffer calculation using robust parser
          const durationMinutes = parseDurationToMinutes(data.duration, defaultSlotDuration);

          // Get the booking start time in minutes
          const bookingStartMinutes = timeToMinutes(data.time);
          const bookingEndMinutes = bookingStartMinutes + durationMinutes;

          // Block ALL time slots that this booking occupies, not just the start
          // For hourly slots, a 1hr booking at 12:00 only blocks 12:00
          // A 2hr booking at 12:00 blocks 12:00 and 13:00
          allTimeSlots.forEach(slot => {
            const slotMinutes = timeToMinutes(slot);
            // A slot is occupied if it starts during the booking period
            if (slotMinutes >= bookingStartMinutes && slotMinutes < bookingEndMinutes) {
              if (!bookedSlots.includes(slot)) {
                bookedSlots.push(slot);
              }
              bookingCounts[slot] = (bookingCounts[slot] || 0) + 1;
            }
          });

          bookingsData.push({
            time: data.time,
            duration: durationMinutes,
            endMinutes: bookingEndMinutes
          });
        }
      });

      // 4. Calculate buffer-blocked slots based on existing bookings
      // Buffer is ONE slot before the booking start and ONE slot after the booking end
      const bufferBlockedSlots = [];
      bookingsData.forEach(booking => {
        const bookingStartMinutes = timeToMinutes(booking.time);
        const bookingEndMinutes = booking.endMinutes;

        allTimeSlots.forEach(slot => {
          const slotMinutes = timeToMinutes(slot);

          // Buffer BEFORE: the slot immediately before booking starts
          // Only block if it's within bufferTimeBefore of the booking start
          if (slotMinutes < bookingStartMinutes && slotMinutes >= bookingStartMinutes - bufferTimeBefore) {
            if (!bufferBlockedSlots.includes(slot) && !bookedSlots.includes(slot)) {
              bufferBlockedSlots.push(slot);
            }
          }

          // Buffer AFTER: the slot immediately after booking ends
          // Only block if it's within bufferTimeAfter of the booking end
          if (slotMinutes >= bookingEndMinutes && slotMinutes < bookingEndMinutes + bufferTimeAfter) {
            if (!bufferBlockedSlots.includes(slot) && !bookedSlots.includes(slot)) {
              bufferBlockedSlots.push(slot);
            }
          }
        });
      });

      // Combine all blocked slots (admin blocks + event blocks)
      const allBlockedSlots = [...new Set([...blockedSlots, ...eventBlockedSlots])];

      return {
        blocked: allBlockedSlots,
        booked: bookedSlots,
        bookingCounts,
        bufferBlocked: [...new Set(bufferBlockedSlots)],
        eventBlocked: [...new Set(eventBlockedSlots)],
        events,
        workingHours,
        isDayEnabled,
        bookingSettings: {
          bufferTimeBefore,
          bufferTimeAfter,
          allowCustomTimeRequests
        }
      };
    } catch (error) {
      console.error('Error fetching availability:', error);
      return {
        blocked: [],
        booked: [],
        bookingCounts: {},
        bufferBlocked: [],
        eventBlocked: [],
        events: [],
        workingHours: null,
        isDayEnabled: true,
        bookingSettings: DEFAULT_BOOKING_SETTINGS
      };
    }
  },

  /**
   * Check if a specific time slot is available
   * @param {Date} date - The date to check
   * @param {string} time - The time slot (e.g., "09:00")
   * @param {number} capacity - Max bookings allowed per slot (default: 1)
   */
  async isSlotAvailable(date, time, capacity = 1) {
    const { blocked, bookingCounts, workingHours } = await this.getAvailabilityForDate(date);

    // Check if time is within working hours
    if (workingHours?.enabled) {
      if (time < workingHours.start || time >= workingHours.end) {
        return false;
      }
    }

    const currentBookings = bookingCounts[time] || 0;
    return !blocked.includes(time) && currentBookings < capacity;
  },

  /**
   * Get the number of remaining spots for a time slot
   * @param {Date} date - The date to check
   * @param {string} time - The time slot (e.g., "09:00")
   * @param {number} capacity - Max bookings allowed per slot (default: 1)
   */
  async getSpotsRemaining(date, time, capacity = 1) {
    const { blocked, bookingCounts, workingHours } = await this.getAvailabilityForDate(date);

    // Check if time is within working hours
    if (workingHours?.enabled) {
      if (time < workingHours.start || time >= workingHours.end) {
        return 0;
      }
    }

    if (blocked.includes(time)) {
      return 0;
    }
    const currentBookings = bookingCounts[time] || 0;
    return Math.max(0, capacity - currentBookings);
  },

  /**
   * Check if there are any events on a specific date
   * @param {Date} date - The date to check
   */
  async getEventsForDate(date) {
    const { events } = await this.getAvailabilityForDate(date);
    return events;
  },

  /**
   * Check if a time slot is blocked by an event
   * @param {Date} date - The date to check
   * @param {string} time - The time slot (e.g., "09:00")
   */
  async isBlockedByEvent(date, time) {
    const { eventBlocked } = await this.getAvailabilityForDate(date);
    return eventBlocked.includes(time);
  },

  /**
   * Get which days of the week are enabled for booking
   * Returns an object with day names as keys and boolean values
   */
  async getEnabledDays() {
    const schedule = await this.getWorkingSchedule();
    if (!schedule) {
      // Default: weekdays enabled
      return {
        sunday: false,
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false
      };
    }

    return {
      sunday: schedule.sunday?.enabled || false,
      monday: schedule.monday?.enabled || false,
      tuesday: schedule.tuesday?.enabled || false,
      wednesday: schedule.wednesday?.enabled || false,
      thursday: schedule.thursday?.enabled || false,
      friday: schedule.friday?.enabled || false,
      saturday: schedule.saturday?.enabled || false
    };
  }
};

export default availabilityService;
